/**
 * Frontend Request Signing Utilities
 * Generates HMAC-SHA256 signatures for API requests
 * Matches the backend signature verification system
 */

import CryptoJS from 'crypto-js';

class RequestSigner {
  constructor() {
    // This should match the backend system signing key
    // In production, this should be configured through environment variables
    // Backend uses process.env.REQUEST_SIGNING_KEY or generates a random key
    // For now, we'll generate a consistent key for development
    this.systemSigningKey = process.env.REACT_APP_REQUEST_SIGNING_KEY || this.generateSystemKey();
  }

  /**
   * Generate system key for development (matches backend logic)
   */
  generateSystemKey() {
    // Use the same key as the backend
    // This matches the backend REQUEST_SIGNING_KEY environment variable (128 hex chars = 64 bytes)
    return 'NUC_REQUEST_SIGNING_2025_GOVERNMENT_HMAC_SHA256_SECURE_KEY_64BYTE_HEX_0123456789ABCDEF0123456789ABCDEF0123456789ABCDEF0123456789ABCDEF';
  }

  /**
   * Generate a secure nonce
   */
  generateNonce() {
    // Create a truly unique nonce by combining timestamp and random bytes
    const timestamp = Date.now().toString();
    const random = CryptoJS.lib.WordArray.random(16).toString();
    return CryptoJS.SHA256(timestamp + random).toString().substring(0, 32);
  }

  /**
   * Generate HMAC-SHA256 signature for request
   */
  generateSignature(method, path, body, timestamp, nonce, signingKey = null) {
    try {
      const key = signingKey || this.systemSigningKey;
      // For GET and DELETE requests with no body, use empty string
      // For other requests, stringify the body if it's an object
      const bodyString = (method === 'GET' || (method === 'DELETE' && !body)) ? '' :
                        (typeof body === 'object' ? JSON.stringify(body) : (body || ''));
      const stringToSign = `${method}\n${path}\n${bodyString}\n${timestamp}\n${nonce}`;

      return CryptoJS.HmacSHA256(stringToSign, key).toString();
    } catch (error) {
      console.error('Failed to generate signature:', error);
      throw new Error('Failed to generate signature');
    }
  }

  /**
   * Generate all required signature headers for a request
   */
  generateSignatureHeaders(method, path, body) {
    const timestamp = Date.now().toString();
    const nonce = this.generateNonce();
    const signature = this.generateSignature(method, path, body, timestamp, nonce);

    // For GET and DELETE requests with no body, use empty string
    const bodyForSigning = (method === 'GET' || (method === 'DELETE' && !body)) ? '' :
                          (typeof body === 'object' ? JSON.stringify(body) : (body || ''));

    console.log('🔐 Frontend Signature Generation:', {
      method,
      path,
      body: bodyForSigning,
      originalBody: typeof body === 'object' ? JSON.stringify(body) : (body || ''),
      bodyType: typeof body,
      bodyLength: bodyForSigning.length,
      timestamp,
      nonce,
      signature,
      signingKey: this.systemSigningKey.substring(0, 20) + '...',
      stringToSign: `${method}\n${path}\n${bodyForSigning}\n${timestamp}\n${nonce}`,
      fullSigningKey: this.systemSigningKey
    });

    return {
      'x-signature': signature,
      'x-timestamp': timestamp,
      'x-nonce': nonce
    };
  }

  /**
   * Check if endpoint requires signature
   */
  requiresSignature(path) {
    const skipRoutes = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/health',
      '/status',
      '/api-docs',
      '/nuc_staff/login',
      '/nuc_staff/',
      '/nuc_dashboard/',
      '/nuc_dap/account',
      '/nuc_dap/',
      '/api/permissions-simple/',
      '/api/v1/mobile/auth/login',
      '/api/v1/mobile/health',
      '/api/v1/mobile/media/upload',
      '/api/v1/mobile/media',
      '/api/v1/mobile/program-data',
      '/api/v1/mobile/media/sync'
    ];

    // Routes that explicitly require signatures
    const requiresSignatureRoutes = [
      '/nuc_institutions',
      '/nuc_university',
      '/nuc_universities',
      '/session-programmes',
      '/nuc_scoring'
    ];

    // If path is in skip routes, no signature required
    if (skipRoutes.some(route => path.startsWith(route))) {
      console.log('🔓 Signature NOT required for:', path, '(skip route)');
      return false;
    }

    // If path is in require signature routes, signature required
    if (requiresSignatureRoutes.some(route => path.startsWith(route))) {
      console.log('🔐 Signature REQUIRED for:', path, '(requires signature)');
      return true;
    }

    // Default: no signature required for other routes
    console.log('🔓 Signature NOT required for:', path, '(default)');
    return false;
  }
}

// Create singleton instance
const requestSigner = new RequestSigner();

export default requestSigner;
export { RequestSigner };
